import React, { useState, useEffect } from 'react';
import './DeviceControlPage.css';
import DeviceAddModal from '../components/deviceControl/DeviceAddModal';
import ScheduleManager from '../components/deviceControl/ScheduleManager';

// API 기본 URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// 전원 스케줄 폼 컴포넌트
const PowerScheduleForm = ({ deviceId, onScheduleAdded, controlLoading }) => {
  const [formData, setFormData] = useState({
    onTime: '',
    offTime: '',
    repeat: true,
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.onTime || !formData.offTime) {
      alert('켜진 시간과 꺼진 시간을 모두 입력해주세요.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/power`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          powerSchedule: {
            onTime: formData.onTime,
            offTime: formData.offTime,
            repeat: formData.repeat,
            days: formData.days
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert('전원 스케줄이 추가되었습니다.');
        setFormData({
          onTime: '',
          offTime: '',
          repeat: true,
          days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
        });
        onScheduleAdded();
      } else {
        alert(data.error || '스케줄 추가에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const dayLabels = {
    mon: '월',
    tue: '화',
    wed: '수',
    thu: '목',
    fri: '금',
    sat: '토',
    sun: '일'
  };

  return (
    <form onSubmit={handleSubmit} className="power-schedule-form">
      <div className="schedule-time-inputs">
        <div className="time-input-group">
          <label>🔛 켜진 시간:</label>
          <input
            type="time"
            value={formData.onTime}
            onChange={(e) => setFormData(prev => ({ ...prev, onTime: e.target.value }))}
            disabled={controlLoading}
            required
          />
        </div>
        <div className="time-input-group">
          <label>🔴 꺼진 시간:</label>
          <input
            type="time"
            value={formData.offTime}
            onChange={(e) => setFormData(prev => ({ ...prev, offTime: e.target.value }))}
            disabled={controlLoading}
            required
          />
        </div>
      </div>

      <div className="schedule-days">
        <label>📅 실행 요일:</label>
        <div className="days-buttons">
          {Object.entries(dayLabels).map(([day, label]) => (
            <button
              key={day}
              type="button"
              onClick={() => handleDayToggle(day)}
              className={`day-button ${formData.days.includes(day) ? 'active' : ''}`}
              disabled={controlLoading}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="schedule-options">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.repeat}
            onChange={(e) => setFormData(prev => ({ ...prev, repeat: e.target.checked }))}
            disabled={controlLoading}
          />
          🔄 반복 실행
        </label>
      </div>

      <button
        type="submit"
        className="add-schedule-button"
        disabled={controlLoading || formData.days.length === 0}
      >
        ➕ 스케줄 추가
      </button>
    </form>
  );
};

// 전원 스케줄 목록 컴포넌트
const PowerScheduleList = ({ deviceId, schedules, onScheduleDelete, onRefresh, controlLoading }) => {
  useEffect(() => {
    onRefresh();
  }, [deviceId]);

  const dayLabels = {
    mon: '월',
    tue: '화',
    wed: '수',
    thu: '목',
    fri: '금',
    sat: '토',
    sun: '일'
  };

  if (schedules.length === 0) {
    return (
      <div className="no-schedules">
        <p>설정된 전원 스케줄이 없습니다.</p>
        <button onClick={onRefresh} className="refresh-schedules-button" disabled={controlLoading}>
          🔄 새로고침
        </button>
      </div>
    );
  }

  return (
    <div className="schedule-list">
      {schedules.map((schedule) => (
        <div key={schedule.id} className="schedule-item">
          <div className="schedule-info">
            <div className="schedule-times">
              <span className="on-time">🔛 {schedule.onTime}</span>
              <span className="off-time">🔴 {schedule.offTime}</span>
            </div>
            <div className="schedule-days">
              {schedule.days.map(day => dayLabels[day]).join(', ')}
            </div>
            <div className="schedule-options">
              {schedule.repeat && <span className="repeat-badge">🔄 반복</span>}
              <span className={`status-badge ${schedule.enabled ? 'enabled' : 'disabled'}`}>
                {schedule.enabled ? '✅ 활성' : '❌ 비활성'}
              </span>
            </div>
          </div>
          <button
            onClick={() => onScheduleDelete(deviceId, schedule.id)}
            className="delete-schedule-button"
            disabled={controlLoading}
            title="스케줄 삭제"
          >
            🗑️
          </button>
        </div>
      ))}
      <button onClick={onRefresh} className="refresh-schedules-button" disabled={controlLoading}>
        🔄 새로고침
      </button>
    </div>
  );
};

const DeviceControlPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlLoading, setControlLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // 장비 목록 로드
  useEffect(() => {
    fetchDevices();
  }, []);

  // 장비 선택 시 전원 스케줄 가져오기
  useEffect(() => {
    if (selectedDevice) {
      fetchPowerSchedules(selectedDevice.deviceId);
    }
  }, [selectedDevice]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5001/api/devices');
      const data = await response.json();

      if (data.success) {
        setDevices(data.devices);
      } else {
        setError(data.error || '장비 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 장비 선택
  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
  };

  // 장비 추가
  const handleAddDevice = async (deviceData) => {
    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deviceData),
      });

      const data = await response.json();

      if (data.success) {
        alert(`장비 "${deviceData.name}"이 성공적으로 추가되었습니다.`);
        // 장비 목록 새로고침
        await fetchDevices();
        setShowAddModal(false);
      } else {
        throw new Error(data.error || '장비 추가에 실패했습니다.');
      }
    } catch (err) {
      console.error('장비 추가 오류:', err);
      throw err; // 모달에서 처리하도록 다시 던짐
    } finally {
      setControlLoading(false);
    }
  };

  // 장비 삭제
  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('정말로 이 장비를 삭제하시겠습니까?')) {
      return;
    }

    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        alert('장비가 성공적으로 삭제되었습니다.');
        // 선택된 장비가 삭제된 경우 선택 해제
        if (selectedDevice && selectedDevice.deviceId === deviceId) {
          setSelectedDevice(null);
        }
        // 장비 목록 새로고침
        await fetchDevices();
      } else {
        throw new Error(data.error || '장비 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('장비 삭제 오류:', err);
      alert(err.message || '장비 삭제에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // 휘도 제어 (1-10단계)
  const handleBrightnessControl = async (deviceId, brightnessLevel) => {
    try {
      console.log(`🎛️ 휘도 제어 시도: ${deviceId}, 레벨: ${brightnessLevel}`);
      setControlLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/brightness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ brightnessLevel }),
      });

      console.log('🌐 휘도 제어 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 휘도 제어 응답 데이터:', data);

      if (data.success) {
        // 성공 메시지 표시
        alert(`✅ 휘도 설정 완료\n• 장비: ${deviceId}\n• 레벨: ${brightnessLevel}단계 (${data.actualBrightness}%)\n• 시간: ${new Date(data.timestamp).toLocaleString()}`);
        // 장비 목록 새로고침
        fetchDevices();
      } else {
        alert(`❌ 휘도 제어 실패\n• 오류: ${data.error || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('❌ 휘도 제어 오류:', err);
      alert(`❌ 휘도 제어에 실패했습니다.\n• 오류: ${err.message}`);
    } finally {
      setControlLoading(false);
    }
  };

  // 전원 스케줄 설정
  const handlePowerSchedule = async (deviceId, powerSchedule) => {
    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/power`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ powerSchedule }),
      });

      const data = await response.json();

      if (data.success) {
        alert(data.message);
        fetchDevices();
      } else {
        alert(data.error || '전원 스케줄 설정에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // 비상 전원 차단
  const handleEmergencyPowerOff = async (deviceId) => {
    if (!window.confirm('정말로 비상 전원 차단을 실행하시겠습니까?')) {
      return;
    }

    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/power`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emergency: true }),
      });

      const data = await response.json();

      if (data.success) {
        alert('비상 전원 차단이 완료되었습니다.');
        fetchDevices();
      } else {
        alert(data.error || '비상 전원 차단에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // 전원 스케줄 조회
  const [powerSchedules, setPowerSchedules] = useState({});

  const fetchPowerSchedules = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/power/schedules`);
      const data = await response.json();

      if (data.success) {
        setPowerSchedules(prev => ({
          ...prev,
          [deviceId]: data.schedules
        }));
      }
    } catch (err) {
      console.error('전원 스케줄 조회 실패:', err);
    }
  };

  // 전원 스케줄 삭제
  const handleDeletePowerSchedule = async (deviceId, scheduleId) => {
    if (!window.confirm('이 전원 스케줄을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/power/schedules/${scheduleId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        alert('전원 스케줄이 삭제되었습니다.');
        fetchPowerSchedules(deviceId);
        fetchDevices();
      } else {
        alert(data.error || '전원 스케줄 삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // 장비 상태 새로고침
  const handleRefreshStatus = async (deviceId) => {
    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/status`);
      const data = await response.json();

      if (data.success) {
        // 상태 업데이트 후 장비 목록 새로고침
        fetchDevices();
      } else {
        alert(data.error || '상태 조회에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // === 테스트 기능들 ===

  // 테스트 장비 강제 온라인 설정
  const handleForceOnline = async (deviceId) => {
    if (!window.confirm('이 장비를 강제로 온라인 상태로 설정하시겠습니까?\n(테스트 기능 - 가상 응답이 시뮬레이션됩니다)')) {
      return;
    }

    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/force-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'online' }),
      });

      const data = await response.json();

      if (data.success) {
        alert('테스트 장비가 강제로 온라인 상태로 설정되었습니다.');
        fetchDevices();
      } else {
        alert(data.error || '강제 온라인 설정에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // 테스트 장비 강제 오프라인 설정
  const handleForceOffline = async (deviceId) => {
    if (!window.confirm('이 장비를 강제로 오프라인 상태로 설정하시겠습니까?')) {
      return;
    }

    try {
      setControlLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/devices/${deviceId}/force-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'offline' }),
      });

      const data = await response.json();

      if (data.success) {
        alert('테스트 장비가 강제로 오프라인 상태로 설정되었습니다.');
        fetchDevices();
      } else {
        alert(data.error || '강제 오프라인 설정에 실패했습니다.');
      }
    } catch (err) {
      alert('서버 연결에 실패했습니다.');
    } finally {
      setControlLoading(false);
    }
  };

  // MQTT 연결 상태 확인
  const handleCheckMqttStatus = async () => {
    try {
      console.log('🔍 MQTT 상태 확인 시도...');
      setControlLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/mqtt/status`);
      console.log('🌐 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 MQTT 상태 응답 데이터:', data);

      if (data.success && data.connected) {
        alert(`✅ MQTT 연결됨\n• 클라이언트 ID: ${data.clientId}\n• 브로커: ${data.brokerHost}\n• 시간: ${new Date(data.timestamp).toLocaleString()}`);
      } else if (data.success && !data.connected) {
        alert(`❌ MQTT 연결 안됨\n• 클라이언트 ID: ${data.clientId}\n• 브로커: ${data.brokerHost}\n• 시간: ${new Date(data.timestamp).toLocaleString()}`);
      } else {
        alert(`⚠️ MQTT 상태 확인 실패\n• 오류: ${data.error || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('❌ MQTT 상태 확인 실패:', err);
      alert(`❌ MQTT 상태 확인에 실패했습니다.\n• 오류: ${err.message}`);
    } finally {
      setControlLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="device-control-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>장비 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="device-control-page">
        <div className="error">
          <h2>❌ 오류 발생</h2>
          <p>{error}</p>
          <button onClick={fetchDevices} className="retry-button">
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="device-control-page">
      <div className="page-header">
        <h1>🖥️ 전광판 장비 제어</h1>
      </div>

      <div className="device-control-container">
        {/* 장비 목록 */}
        <div className="device-list">
          <div className="section-header">
            <h2>📱 장비 목록</h2>
            <div className="header-buttons">
              <button
                onClick={() => setShowAddModal(true)}
                className="add-device-button"
                disabled={controlLoading}
              >
                ➕ 장비 추가
              </button>
              <button
                onClick={fetchDevices}
                className="refresh-button"
                disabled={controlLoading}
              >
                🔄 새로고침
              </button>
            </div>
          </div>

          {devices.length === 0 ? (
            <div className="no-devices">
              <p>등록된 장비가 없습니다.</p>
            </div>
          ) : (
            <div className="devices-grid">
              {devices.map((device) => (
                <div
                  key={device.deviceId}
                  className={`device-card ${selectedDevice?.deviceId === device.deviceId ? 'selected' : ''}`}
                  onClick={() => handleDeviceSelect(device)}
                >
                  <div className="device-header">
                    <div className="device-title">
                      <h3>{device.name}</h3>
                      <span className={`status-badge ${device.status.online ? 'online' : 'offline'}`}>
                        {device.status.online ? '🟢 온라인' : '🔴 오프라인'}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDevice(device.deviceId);
                      }}
                      className="delete-device-button"
                      disabled={controlLoading}
                      title="장비 삭제"
                    >
                      🗑️
                    </button>
                  </div>

                  <div className="device-info">
                    <p><strong>ID:</strong> {device.deviceId}</p>
                    <p><strong>위치:</strong> {device.location?.address || '정보 없음'}</p>
                    <p><strong>휘도:</strong> {device.status.brightness}%</p>
                    <p><strong>전원:</strong> {device.status.power ? '🟢 ON' : '🔴 OFF'}</p>
                    <p><strong>온도:</strong> {device.status.temperature}°C</p>
                    <p><strong>마지막 업데이트:</strong> {
                      device.status.lastUpdate ?
                        new Date(device.status.lastUpdate).toLocaleString() :
                        '정보 없음'
                    }</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 장비 제어 패널 */}
        {selectedDevice && (
          <div className="device-control-panel">
            <div className="section-header">
              <h2>🎛️ 장비 제어</h2>
              <h3>{selectedDevice.name}</h3>
            </div>

            <div className="control-sections">
              {/* 휘도 제어 */}
              <div className="control-section">
                <h4>💡 휘도 제어 (1-10단계)</h4>
                <div className="brightness-control">
                  <div className="brightness-info">
                    <span>현재 휘도: {selectedDevice.status.brightnessLevel || 5}단계</span>
                  </div>
                  <div className="brightness-buttons">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                      <button
                        key={level}
                        onClick={() => handleBrightnessControl(selectedDevice.deviceId, level)}
                        className={`brightness-button level-${level} ${(selectedDevice.status.brightnessLevel || 5) === level ? 'active' : ''
                          }`}
                        disabled={controlLoading}
                        title={`${level}단계 (${level * 10}%)`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                  <div className="brightness-labels">
                    <span>낮음 (1-3단계)</span>
                    <span>중간 (4-7단계)</span>
                    <span>높음 (8-10단계)</span>
                  </div>
                </div>
              </div>

              {/* 전원 제어 */}
              <div className="control-section">
                <h4>⚡ 전원 제어 (시간 스케줄)</h4>
                <div className="power-control">
                  <div className="power-status">
                    <span>현재 상태: {selectedDevice.status.power ? '🟢 ON' : '🔴 OFF'}</span>
                  </div>

                  {/* 전원 스케줄 설정 */}
                  <div className="power-schedule-form">
                    <h5>📅 새 전원 스케줄 추가</h5>
                    <PowerScheduleForm
                      deviceId={selectedDevice.deviceId}
                      onScheduleAdded={() => {
                        fetchPowerSchedules(selectedDevice.deviceId);
                        fetchDevices();
                      }}
                      controlLoading={controlLoading}
                    />
                  </div>

                  {/* 기존 스케줄 목록 */}
                  <div className="existing-schedules">
                    <h5>📋 설정된 전원 스케줄</h5>
                    <PowerScheduleList
                      deviceId={selectedDevice.deviceId}
                      schedules={powerSchedules[selectedDevice.deviceId] || []}
                      onScheduleDelete={handleDeletePowerSchedule}
                      onRefresh={() => fetchPowerSchedules(selectedDevice.deviceId)}
                      controlLoading={controlLoading}
                    />
                  </div>

                  {/* 비상 제어 */}
                  <div className="emergency-control">
                    <h5>🚨 비상 제어</h5>
                    <button
                      onClick={() => handleEmergencyPowerOff(selectedDevice.deviceId)}
                      className="emergency-power-off-button"
                      disabled={controlLoading}
                      title="즉시 전원 차단 (비상시에만 사용)"
                    >
                      ⚡ 비상 전원 차단
                    </button>
                    <p className="emergency-note">
                      ⚠️ 비상 전원 차단은 응급상황에서만 사용하세요
                    </p>
                  </div>
                </div>
              </div>

              {/* 상태 정보 */}
              <div className="control-section">
                <h4>📊 상태 정보</h4>
                <div className="status-info">
                  <div className="status-grid">
                    <div className="status-item">
                      <span className="status-label">온라인 상태:</span>
                      <span className={`status-value ${selectedDevice.status.online ? 'online' : 'offline'}`}>
                        {selectedDevice.status.online ? '🟢 온라인' : '🔴 오프라인'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">온도:</span>
                      <span className="status-value">{selectedDevice.status.temperature}°C</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">전압:</span>
                      <span className="status-value">{selectedDevice.status.voltage}V</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">가동 시간:</span>
                      <span className="status-value">{Math.floor(selectedDevice.status.uptime / 3600)}시간</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRefreshStatus(selectedDevice.deviceId)}
                    className="refresh-status-button"
                    disabled={controlLoading}
                  >
                    🔄 상태 새로고침
                  </button>
                </div>
              </div>

              {/* 스케줄 관리 */}
              <div className="control-section">
                <ScheduleManager
                  deviceId={selectedDevice.deviceId}
                  onScheduleChange={fetchDevices}
                />
              </div>

              {/* 테스트 기능 (테스트 장비에만 표시) */}
              {(selectedDevice.deviceId.includes('TEST_') || selectedDevice.deviceId.includes('test_')) && (
                <div className="control-section test-section">
                  <h4>🧪 테스트 기능</h4>
                  <div className="test-controls">
                    <div className="test-info">
                      <p>⚠️ 이 섹션은 테스트 장비에서만 사용할 수 있습니다.</p>
                      <p>실제 장비 응답을 시뮬레이션하여 테스트할 수 있습니다.</p>
                    </div>

                    <div className="test-buttons">
                      <button
                        onClick={() => handleForceOnline(selectedDevice.deviceId)}
                        className="test-button force-online"
                        disabled={controlLoading}
                        title="장비를 강제로 온라인 상태로 설정하고 가상 응답 시뮬레이션"
                      >
                        🟢 강제 온라인 설정
                      </button>

                      <button
                        onClick={() => handleForceOffline(selectedDevice.deviceId)}
                        className="test-button force-offline"
                        disabled={controlLoading}
                        title="장비를 강제로 오프라인 상태로 설정"
                      >
                        🔴 강제 오프라인 설정
                      </button>

                      <button
                        onClick={handleCheckMqttStatus}
                        className="test-button mqtt-status"
                        disabled={controlLoading}
                        title="MQTT 브로커 연결 상태 확인"
                      >
                        📡 MQTT 상태 확인
                      </button>
                    </div>

                    <div className="test-status">
                      {selectedDevice.status.forcedStatus && (
                        <div className="forced-status-warning">
                          ⚡ 이 장비는 현재 강제 설정된 상태입니다
                        </div>
                      )}
                      <div className="test-device-info">
                        <p><strong>장비 유형:</strong> 테스트 장비</p>
                        <p><strong>시뮬레이션:</strong> 가상 응답 활성화</p>
                        <p><strong>프로토콜:</strong> 신프로토콜 (2023.3)</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 로딩 오버레이 */}
      {controlLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>제어 명령을 전송하는 중...</p>
          </div>
        </div>
      )}

      {/* 장비 추가 모달 */}
      <DeviceAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddDevice}
      />
    </div>
  );
};

export default DeviceControlPage; 